import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { useDispatch } from 'react-redux';
import { getAllDateTableTask, updateTask } from '../../Redux/GetSlice';
import 'react-datepicker/dist/react-datepicker.css';
import ReminderDateTimePicker from './ReminderDateTimePicker';
import { useTaskData } from '../../hooks/useTaskData';

export default function MyDayPlanDropdown({
    currentTaskId,
    setSelectedMyDayPlanDate,
    selectedMyDayPlanDate,
    handleUpdateTask,
    isOpen,
    onClose,
    taskItem,
    taskId,
    type,
    setIsMyDayChecked,
    isMyDayChecked,
    clickedBellIcon,
    fixedPosition,
}) {
    const [customDate, setCustomDate] = useState(null);
    const [showDatePicker, setShowDatePicker] = useState(false);
    const dropdownRef = useRef(null);
    const dispatch = useDispatch();

    const {
        isMyDayChecked: reduxIsMyDayChecked,
        updateSelectedMyDayPlanDate: updateSelectedMyDayPlanDateRedux,
        updateSelectedReminderDate: updateSelectedReminderDateRedux,
        updateMyDayDate,
        updateIsMyDayChecked: updateIsMyDayCheckedRedux,
    } = useTaskData();

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                if (onClose) {
                    onClose();
                }
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose]);

    const [anchorPos, setAnchorPos] = useState(null);

    useLayoutEffect(() => {
        if (isOpen && !fixedPosition && !clickedBellIcon && dropdownRef.current) {
            const rect = dropdownRef.current.getBoundingClientRect();
            const dropdownWidth = 320;
            let left = rect.left;
            if (left + dropdownWidth > window.innerWidth - 8) {
                left = window.innerWidth - dropdownWidth - 8;
            }
            setAnchorPos({ top: rect.top + 8, left: Math.max(8, left) });
        } else {
            setAnchorPos(null);
        }
    }, [isOpen, fixedPosition, clickedBellIcon]);

    const formatToLocalISO = (date) => {
        const pad = (num) => String(num).padStart(2, '0');
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(
            date.getMinutes(),
        )}:00`;
    };

    const formatDateForDisplay = (date) => {
        if (!date) return '';
        return new Date(date).toLocaleString('en-US', {
            weekday: 'short',
            hour: 'numeric',
            minute: 'numeric',
            hour12: true,
        });
    };

    const getFormattedDate = (dateOption) => {
        if (dateOption && typeof dateOption === 'object' && 'daysToAdd' in dateOption) {
            const date = new Date();
            date.setDate(date.getDate() + dateOption.daysToAdd);
            date.setHours(dateOption.time.hours, dateOption.time.minutes, 0, 0);
            return date;
        }
        return dateOption;
    };

    const handleReminderSelection = (dateOption) => {
        const date = getFormattedDate(dateOption);
        const formattedDate = date.toISOString();
        const taskToUpdate = type === 'DatePlan-Table' ? taskId || currentTaskId : taskItem?.id;

        if (type === 'DatePlan-Table') {
            updateSelectedReminderDateRedux(formattedDate);
            handleUpdateTask(formattedDate, 'reminder', taskItem);
            dispatch(
                updateTask({
                    newDate: formattedDate,
                    type: 'reminder',
                    taskItem: taskToUpdate,
                }),
            );
            dispatch(getAllDateTableTask());
        } else {
            updateSelectedMyDayPlanDateRedux(formattedDate);
            updateMyDayDate(formattedDate);
            handleUpdateTask(formattedDate, 'myDay', taskItem);
        }

        if (onClose) {
            onClose();
        }
    };

    useEffect(() => {
        if (selectedMyDayPlanDate) {
            const date = new Date(selectedMyDayPlanDate);
            setCustomDate(date);
        }
    }, [selectedMyDayPlanDate]);

    const handleDateChange = (date) => {
        setCustomDate(date);
        setShowDatePicker(false);
        handleReminderSelection(date);
    };

    const handleDateOnlyChange = (date) => {
        const next = customDate ? new Date(customDate) : new Date();
        next.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());
        if (!customDate) {
            next.setHours(8, 0, 0, 0);
        }
        setCustomDate(next);
    };

    const toggleDatePicker = () => {
        if (!showDatePicker) {
            setCustomDate(customDate || new Date());
        }
        setShowDatePicker(!showDatePicker);
    };

    const handleClearSelection = () => {
        const taskToUpdate = type === 'DatePlan-Table' ? taskId || currentTaskId : taskItem?.id;

        if (type === 'DatePlan-Table') {
            updateSelectedReminderDateRedux(null);
            handleUpdateTask(null, 'reminder', taskItem);
            dispatch(updateTask({ newDate: null, type: 'reminder', taskItem: taskToUpdate }));
            dispatch(getAllDateTableTask());
        } else {
            updateSelectedMyDayPlanDateRedux(null);
            updateMyDayDate(null);
            if (setSelectedMyDayPlanDate) {
                setSelectedMyDayPlanDate(null);
            }
            dispatch(updateTask({ newDate: null, type: 'myDayPlan', taskItem: taskToUpdate }));
        }
    };

    const today = { daysToAdd: 0, time: { hours: 12, minutes: 0 } };
    const tomorrow = { daysToAdd: 1, time: { hours: 8, minutes: 0 } };
    const inTwoDays = { daysToAdd: 2, time: { hours: 8, minutes: 0 } };
    const nextWeek = { daysToAdd: 7, time: { hours: 8, minutes: 0 } };
    const currentIsMyDayChecked = reduxIsMyDayChecked !== undefined ? reduxIsMyDayChecked : isMyDayChecked;
    const currentSetIsMyDayChecked = updateIsMyDayCheckedRedux || setIsMyDayChecked;

    const presetOptions = [
        { key: 'today', label: 'Later today', icon: '☀️', option: today },
        { key: 'tomorrow', label: 'Tomorrow', icon: '🌙', option: tomorrow },
        { key: 'twoDays', label: 'In two days', icon: '🕒', option: inTwoDays },
        { key: 'nextWeek', label: 'Next week', icon: '📅', option: nextWeek },
    ];

    const dropdownEl = (
            <div
                className={`reminder-dropdown ${clickedBellIcon ? 'reminder-dropdown--bell' : fixedPosition ? 'reminder-dropdown--fixed' : 'reminder-dropdown--anchored'}`}
                style={
                    anchorPos
                        ? { position: 'fixed', top: anchorPos.top, left: anchorPos.left, zIndex: 1100 }
                        : fixedPosition
                            ? { top: fixedPosition.top, right: fixedPosition.right, width: fixedPosition.width || Math.min(300, window.innerWidth - 16), maxHeight: window.innerHeight - fixedPosition.top - 8 }
                            : clickedBellIcon
                                ? { top: '50px', right: '2vw', minWidth: 320 }
                                : { minWidth: 320 }
                }
            >
                <div className={`reminder-dropdown__card ${showDatePicker ? 'reminder-dropdown__card--expanded' : ''}`}>
                    <div className="reminder-dropdown__header">
                        <span className="reminder-dropdown__title">{type === 'DatePlan-Table' ? 'Set Reminder...' : 'Add to My Day plan...'}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <button type="button" onClick={handleClearSelection} className="reminder-dropdown__clear">Clear</button>
                            <button
                                type="button"
                                onClick={() => { if (onClose) onClose(); }}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', fontSize: '16px', lineHeight: 1, padding: '2px 4px' }}
                                title="Close"
                            >✕</button>
                        </div>
                    </div>

                    <ul className="reminder-dropdown__options">
                        {presetOptions.map(({ key, label, icon, option }) => (
                            <li key={key}>
                                <button
                                    type="button"
                                    className="reminder-dropdown__option"
                                    onClick={() => handleReminderSelection(option)}
                                >
                                    <span className="reminder-dropdown__option-left">
                                        <span className="reminder-dropdown__option-icon" aria-hidden="true">
                                            {icon}
                                        </span>
                                        <span className="reminder-dropdown__option-label">{label}</span>
                                    </span>
                                    <span className="reminder-dropdown__option-time">
                                        {formatDateForDisplay(getFormattedDate(option))}
                                    </span>
                                </button>
                            </li>
                        ))}
                        <li className={showDatePicker ? 'reminder-dropdown__pick-row--open' : ''}>
                            <button
                                type="button"
                                className={`reminder-dropdown__option ${showDatePicker ? 'reminder-dropdown__option--active' : ''}`}
                                onClick={toggleDatePicker}
                                aria-expanded={showDatePicker}
                            >
                                <span className="reminder-dropdown__option-left">
                                    <span className="reminder-dropdown__option-icon" aria-hidden="true">
                                        📆
                                    </span>
                                    <span className="reminder-dropdown__option-label">Pick a date</span>
                                </span>
                            </button>
                        </li>
                    </ul>

                    {showDatePicker && (
                        <ReminderDateTimePicker
                            customDate={customDate}
                            setCustomDate={setCustomDate}
                            onDateOnlyChange={handleDateOnlyChange}
                            onSave={() => handleDateChange(customDate)}
                            onToday={() => setCustomDate(new Date())}
                            onClear={() => setCustomDate(null)}
                        />
                    )}

                    <div className="reminder-dropdown__footer">
                        <label className="reminder-dropdown__checkbox-label">
                            <input
                                type="checkbox"
                                className="reminder-dropdown__checkbox"
                                checked={currentIsMyDayChecked}
                                onChange={(e) => currentSetIsMyDayChecked(e.target.checked)}
                            />
                            Also update the deadline date
                        </label>
                    </div>
                </div>
            </div>
    );

    if (!isOpen) {
        if (fixedPosition) return null;
        return <div className="relative w-full text-left" ref={dropdownRef} />;
    }

    if (fixedPosition) {
        return dropdownEl;
    }

    return (
        <div className="relative w-full text-left" ref={dropdownRef}>
            {dropdownEl}
        </div>
    );
}
